/* eslint-disable @typescript-eslint/member-ordering */

import { ContentObserver } from '@angular/cdk/observers';
import { SharedResizeObserver } from '@angular/cdk/observers/private';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EffectRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { merge, Subscription } from 'rxjs';

import { AppConfigModel } from '@libs/cms/models';
import { ImageItemModel } from '@libs/common/models';
import { IconComponent } from '@libs/components/icons';
import { PageViewService } from '@libs/shared/services';
import { LinkDirective } from '@libs/shared/ui';

import { ImageComponent } from '../../data-display/image';
import { LanguageNavigationComponent } from '../../navigation/language-navigation';
import { LekkerlandDesktopNavigationComponent } from '../lekkerland-desktop-navigation';

@Component({
  selector: 'ui-lekkerland-header',
  templateUrl: './lekkerland-header.component.html',
  styleUrls: ['./lekkerland-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    ImageComponent,
    LanguageNavigationComponent,
    LinkDirective,
    LekkerlandDesktopNavigationComponent,
  ],
  standalone: true,
})
export class LekkerlandHeaderComponent {
  private readonly contentObserver: ContentObserver = inject(ContentObserver);
  private readonly pageViewService: PageViewService = inject(PageViewService);
  private readonly sharedResizeObserver: SharedResizeObserver = inject(SharedResizeObserver);

  private readonly headerContentElement: Signal<ElementRef<HTMLElement>> =
    viewChild.required<ElementRef<HTMLElement>>('headerContent');

  private readonly desktopNavigationElement: Signal<ElementRef<HTMLElement>> =
    viewChild.required<ElementRef<HTMLElement>>('desktopNavigation');

  private readonly desktopLayoutHysteresis: number = 16;
  private desktopMinWidth: number = 0;

  public readonly config: InputSignal<AppConfigModel> = input.required();

  public readonly openNavigation: OutputEmitterRef<void> = output<void>();

  public readonly headerLogo: Signal<ImageItemModel | undefined> =
    computed(() => this.config().header.logo);

  public readonly showLanguageSwitch: Signal<boolean> = toSignal(
    this.pageViewService.hasMultipleAvailableLanguages(),
    { initialValue: false },
  );

  public readonly useMobileLayout: WritableSignal<boolean> = signal(false);

  private readonly layoutObserverEffect: EffectRef = effect(onCleanup => {
    const headerContent: HTMLElement = this.headerContentElement().nativeElement;
    const desktopNavigation: HTMLElement = this.desktopNavigationElement().nativeElement;

    const resizeObserverSubscription: Subscription = merge(
      this.sharedResizeObserver.observe(headerContent),
      this.sharedResizeObserver.observe(desktopNavigation),
    ).subscribe(() => {
      this.updateLayout(headerContent);
    });

    const contentObserverSubscription: Subscription =
      this.contentObserver.observe(headerContent).subscribe(() => {
        this.desktopMinWidth = 0;
        this.useMobileLayout.set(false);
      });

    onCleanup(() => {
      resizeObserverSubscription.unsubscribe();
      contentObserverSubscription.unsubscribe();
    });
  });

  private updateLayout(headerContent: HTMLElement): void {
    const header: HTMLElement | null = headerContent.parentElement;

    if (!header) {
      return;
    }

    const availableHeaderWidth: number = header.clientWidth;

    if (!this.useMobileLayout()) {
      const overflowWidth: number =
        headerContent.scrollWidth - headerContent.clientWidth;

      if (overflowWidth > 1) {
        this.desktopMinWidth = availableHeaderWidth + overflowWidth;
        this.useMobileLayout.set(true);
      }

      return;
    }

    if (
      this.desktopMinWidth > 0 &&
      availableHeaderWidth >=
      this.desktopMinWidth + this.desktopLayoutHysteresis
    ) {
      this.useMobileLayout.set(false);
    }
  }
}
